import React, { FunctionComponent, useCallback, useState } from 'react';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import FileUpload from 'ui/atoms/file-upload';

export interface FileToUpload {
  file: File;
  documentType?: string;
}

interface MultipleFileUploadProps {
  onChange: (arg: FileToUpload[] | null) => void;
  onFocus?: (arg: any) => void;
  required?: boolean;
  showRequiredError?: boolean;
  showValidatedForm?: boolean;
  documentTypes?: Array<any>;
  valid?: boolean;
  value?: any;
  groupFiles?: boolean;
  isDocumentTypeRequired?: boolean;
}

export const MultipleFileUpload: FunctionComponent<MultipleFileUploadProps> = ({
  onChange = () => {},
  showRequiredError,
  showValidatedForm,
  documentTypes = [],
  groupFiles,
  isDocumentTypeRequired,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<FileToUpload[]>([]);
  const [error, setError] = useState('');

  const translate = useTranslateWithStringArgs();

  const handleFileInput = (event: any) => {
    event.preventDefault();
    setError('');

    const files = event.target?.files?.length ? event.target.files : event.dataTransfer.files;
    if (files.length === 0) return;

    const MAX_FILE_COUNT = 100;
    let errorMessage = '';

    if (files.length > MAX_FILE_COUNT) {
      setError(translate('form.errors.maxFilesCount'));
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const hasValidFormat = file.type === 'application/pdf';

      if (!hasValidFormat) {
        errorMessage = translate('form.errors.fileTypePdf');
        break;
      }
    }

    if (errorMessage.length > 0) {
      setError(errorMessage);
      if (event.target) {
        event.target.value = null;
      }
    } else {
      const newFilesToUpload: FileToUpload[] = [];
      for (let i = 0; i < files.length; i++) {
        newFilesToUpload.push({ file: files[i] });
      }

      const updatedFiles = [...newFilesToUpload];
      setUploadedFiles(updatedFiles);
      onChange(updatedFiles);
    }
  };

  const onSelectDocumentType = useCallback(
    (documentType: string) => {
      if (uploadedFiles.length <= 0) return;

      uploadedFiles.map((fileToUpload) => (fileToUpload.documentType = documentType));

      setUploadedFiles([...uploadedFiles]);
      onChange([...uploadedFiles]);
    },
    [uploadedFiles, onChange],
  );

  const onRemoveDocument = () => {
    setUploadedFiles([]);
    onChange(null);
    setError('');
  };

  return (
    <FileUpload
      handleFileInput={handleFileInput}
      uploadedFiles={uploadedFiles}
      onSelectDocumentType={onSelectDocumentType}
      onRemoveDocument={onRemoveDocument}
      documentTypes={documentTypes}
      showRequiredError={showRequiredError}
      showValidatedForm={showValidatedForm}
      multipleFiles
      error={error}
      groupFiles={groupFiles}
      isDocumentTypeRequired={isDocumentTypeRequired}
    />
  );
};

export default MultipleFileUpload;
